import {
  IconLayoutSidebarLeftCollapse,
  IconArchive,
  IconChevronDown,
  IconGripVertical,
  IconLock,
} from "@tabler/icons-react";
import { useTranslations } from "next-intl";
import { Link } from "react-router-dom";
import { memo, useCallback, useEffect, useRef, useState } from "react";
import { Logo } from "../AppBar/components/Logo";
import { IconButton } from "../Buttons/IconButton";
import { SearchButton } from "../SearchButton/SearchButton";
import { useEventSidebar } from "./useEventSidebar";
import { Plugin } from "@/types/enums/Plugin";
import { useSidebar } from "@/providers/SidebarProvider";
import { isPathActive, isSamePath } from "@/lib/routing/activePath";

type EventSidebarProps = {
  collapsed: boolean;
  plugins: Plugin[];
  eventRole: string;
  isArchived?: boolean;
  isPlanLocked?: boolean;
  sidebarOrder?: string[];
  /**
   * Event/role data not in yet. The menu is gated on BOTH (plugins decide
   * which entries exist, capabilities decide which of those are visible), so
   * rendering the filtered list before either arrives paints a menu that is
   * wrong rather than incomplete: with the empty-plugin fallback and the
   * VIEWER role fallback the only surviving entry is Guests + its two
   * sub-items, and everything else pops in a beat later. Skeleton rows hold
   * the column instead.
   */
  loading?: boolean;
};

type MenuItemShape = {
  name: string;
  icon?: React.ElementType;
  url: string;
  subItems?: { name: string; url: string; testId?: string }[];
};

type MenuRowProps = {
  item: MenuItemShape;
  index: number;
  eventId: string;
  currentUrl: string;
  isParentActive: boolean;
  isSubmenuOpen: boolean;
  isCollapsed: boolean;
  isDnDEnabled: boolean;
  isDragging: boolean;
  isDropTarget: boolean;
  dragItemRef: React.MutableRefObject<number | null>;
  dragOverItemRef: React.MutableRefObject<number | null>;
  setDragOverIndex: React.Dispatch<React.SetStateAction<number | null>>;
  setIsDragging: React.Dispatch<React.SetStateAction<boolean>>;
  onDragEnd: () => void;
  navigateTo: (url: string) => void;
  prefetch: (url: string) => void;
};

const MenuRow = memo(function MenuRow({
  item,
  index,
  eventId,
  currentUrl,
  isParentActive,
  isSubmenuOpen,
  isCollapsed,
  isDnDEnabled,
  isDragging,
  isDropTarget,
  dragItemRef,
  dragOverItemRef,
  setDragOverIndex,
  setIsDragging,
  onDragEnd,
  navigateTo,
  prefetch,
}: MenuRowProps) {
  const primaryUrl = `/event/${eventId}/${
    item.subItems ? item.url + "/" + item.subItems[0].url : item.url
  }`;
  const isFirstSubItemActive =
    !!item.subItems &&
    currentUrl === `/event/${eventId}/${item.url}/${item.subItems[0].url}`;

  // Hover-open is CSS-driven (group/menu + grid-template-rows + 400 ms
  // transition-delay). The row stays force-open when the URL is in the
  // submenu, and force-closed while dragging.
  // Touch has no hover: `tapOpen` (chevron button, coarse pointers only) is
  // the tap equivalent, reset on navigation so rows collapse again.
  const [tapOpen, setTapOpen] = useState(false);
  useEffect(() => {
    setTapOpen(false);
  }, [currentUrl]);
  const submenuRowsClass =
    isSubmenuOpen || tapOpen
      ? "grid-rows-[1fr]"
      : isDragging
        ? "grid-rows-[0fr]"
        : "grid-rows-[0fr] group-hover/menu:grid-rows-[1fr]";

  return (
    <div
      draggable={isDnDEnabled}
      onDragStart={(e) => {
        dragItemRef.current = index;
        setIsDragging(true);
        e.dataTransfer.effectAllowed = "move";
      }}
      onDragEnter={() => {
        dragOverItemRef.current = index;
        setDragOverIndex(index);
      }}
      onDragOver={(e) => {
        e.preventDefault();
        e.dataTransfer.dropEffect = "move";
      }}
      onDragEnd={onDragEnd}
      className={`relative group/menu ${isDnDEnabled ? "group/drag" : ""} ${
        isDropTarget
          ? "border-t-2 border-accent"
          : "border-t-2 border-transparent"
      }`}
    >
      <div className="relative">
        {isDnDEnabled && (
          <div className="absolute left-0 top-1/2 -translate-y-1/2 -translate-x-1 opacity-0 group-hover/drag:opacity-100 transition-opacity z-10 cursor-grab active:cursor-grabbing">
            <IconGripVertical size={14} className="font-tertiary" />
          </div>
        )}
        <IconButton
          href={primaryUrl}
          onClickAction={() => navigateTo(primaryUrl)}
          onHoverAction={() => prefetch(primaryUrl)}
          active={
            (isParentActive && !isSubmenuOpen) ||
            (isSubmenuOpen && isCollapsed)
          }
          disableWithoutShowing={
            (isParentActive && !isSubmenuOpen) ||
            (isSubmenuOpen && isCollapsed) ||
            isFirstSubItemActive
          }
          color={
            isParentActive && !isSubmenuOpen
              ? "font-primary"
              : "font-tertiary"
          }
          fontSize="body-3-medium"
          space={isCollapsed ? "space-x-0" : "space-x-1.5"}
          // Coarse pointers show the tap-to-expand chevron overlaid at right-2;
          // reserve its footprint so long labels don't render underneath it.
          padding={
            item.subItems && !isCollapsed
              ? "px-3 py-1.5 pointer-coarse:pr-8"
              : "px-3 py-1.5"
          }
          align={isCollapsed ? "justify-center items-center" : ""}
          icon={item.icon!}
          iconSize={16}
          text={isCollapsed ? "" : item.name}
        />
        {item.subItems && !isCollapsed && (
          <button
            type="button"
            aria-expanded={isSubmenuOpen || tapOpen}
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              setTapOpen((v) => !v);
            }}
            className="pointer-fine:hidden absolute right-2 top-1/2 -translate-y-1/2 p-1 font-tertiary"
          >
            <IconChevronDown
              size={14}
              className={`transition-transform duration-200 ${
                isSubmenuOpen || tapOpen ? "rotate-180" : ""
              }`}
            />
          </button>
        )}
      </div>
      {item.subItems && !isCollapsed && (
        <div
          className={`grid transition-[grid-template-rows] duration-200 group-hover/menu:delay-[400ms] ${submenuRowsClass}`}
        >
          <div className="overflow-hidden">
            <div className="relative mt-1">
              {item.subItems.map((subItem, subIndex) => {
                const subUrl = `/event/${eventId}/${item.url}/${subItem.url}`;
                // Highlighted on its own page AND below it (`settings/access/invite`
                // keeps "Access" lit) — the most specific sibling wins; plain
                // text instead of a link only on the exact page.
                const isSubActive = subUrl === activeSubUrl(currentUrl, eventId ?? "", item);
                const isSubExact = isSubActive && isSamePath(currentUrl, subUrl);
                return (
                  <div
                    key={subIndex}
                    className={`relative ${
                      isSubActive ? "surface-secondary" : "hover:surface-secondary"
                    } rounded-lg pl-6 mb-1 group`}
                  >
                    {!isSubActive && (
                      <>
                        <div
                          className="absolute left-0 bg-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-200"
                          style={{
                            marginLeft: "20px",
                            top: `calc(-${subIndex} * (100% + 4px) - 8px)`,
                            height: `calc(50% + ${subIndex} * (100% + 4px) + 8px)`,
                            width: "12px",
                            borderLeft:
                              "1.5px solid var(--color-font-tertiary)",
                            borderBottom:
                              "1.5px solid var(--color-font-tertiary)",
                            borderBottomLeftRadius: "8px",
                          }}
                        />
                        <div
                          className="absolute left-0 opacity-0 group-hover:opacity-100 transition-opacity duration-200"
                          style={{
                            marginLeft: "26px",
                            top: "50%",
                            transform: "translateY(-50%) rotate(-45deg)",
                            width: "6px",
                            height: "6px",
                            borderRight:
                              "1.5px solid var(--color-font-tertiary)",
                            borderBottom:
                              "1.5px solid var(--color-font-tertiary)",
                          }}
                        />
                      </>
                    )}
                    {isSubActive && (
                      <>
                        <div
                          className="absolute left-0 bg-transparent"
                          style={{
                            marginLeft: "20px",
                            top: `calc(-${subIndex} * (100% + 4px) - 8px)`,
                            height: `calc(50% + ${subIndex} * (100% + 4px) + 8px)`,
                            width: "12px",
                            borderLeft:
                              "1.5px solid var(--color-font-tertiary)",
                            borderBottom:
                              "1.5px solid var(--color-font-tertiary)",
                            borderBottomLeftRadius: "8px",
                          }}
                        />
                        <div
                          className="absolute left-0"
                          style={{
                            marginLeft: "26px",
                            top: "50%",
                            transform: "translateY(-50%) rotate(-45deg)",
                            width: "6px",
                            height: "6px",
                            borderRight:
                              "1.5px solid var(--color-font-tertiary)",
                            borderBottom:
                              "1.5px solid var(--color-font-tertiary)",
                          }}
                        />
                      </>
                    )}
                    {isSubExact ? (
                      <span
                        className="block w-full text-left px-3 py-2 rounded-lg body-3-medium transition-colors cursor-default font-primary"
                        data-testid={subItem.testId}
                      >
                        {subItem.name}
                      </span>
                    ) : (
                      <Link
                        to={subUrl}
                        onClick={(e) => {
                          if (
                            e.metaKey ||
                            e.ctrlKey ||
                            e.shiftKey ||
                            e.button !== 0
                          )
                            return;
                          e.preventDefault();
                          navigateTo(subUrl);
                        }}
                        onMouseEnter={() => prefetch(subUrl)}
                        className={`block w-full text-left px-3 py-2 rounded-lg body-3-medium transition-colors cursor-pointer ${
                          isSubActive ? "font-primary" : "font-tertiary"
                        }`}
                        data-testid={subItem.testId}
                      >
                        {subItem.name}
                      </Link>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      )}
    </div>
  );
});

/**
 * The sub-item the current page belongs to: the longest sub-item URL that is
 * the page itself or one of its ancestors, so a nested page lights its own
 * entry and never a shorter sibling as well.
 */
function activeSubUrl(
  currentUrl: string,
  eventId: string,
  item: { url: string; subItems?: { url: string }[] },
): string | null {
  let best: string | null = null;
  for (const sub of item.subItems ?? []) {
    const url = `/event/${eventId}/${item.url}/${sub.url}`;
    if (isPathActive(currentUrl, url) && (best === null || url.length > best.length)) best = url;
  }
  return best;
}

// Placeholder rows for the loading window. Widths are a fixed literal (not
// randomised) so a re-render inside the window doesn't reshuffle the bars.
const SKELETON_ROW_WIDTHS = [72, 88, 64, 80, 96, 72, 88, 64];

const MenuSkeleton = ({ isCollapsed }: { isCollapsed: boolean }) => (
  <div className="animate-pulse" aria-hidden="true">
    {SKELETON_ROW_WIDTHS.map((w, i) => (
      <div
        key={i}
        className={`flex items-center px-3 py-1.5 mb-1 ${
          isCollapsed ? "justify-center" : "space-x-1.5"
        }`}
      >
        <div className="h-4 w-4 surface-grey rounded shrink-0" />
        {!isCollapsed && (
          <div className="h-3 surface-grey rounded" style={{ width: w }} />
        )}
      </div>
    ))}
  </div>
);

export const EventSidebar: React.FC<EventSidebarProps> = ({
  collapsed: collapsedProp,
  plugins,
  eventRole,
  isArchived = false,
  isPlanLocked = false,
  sidebarOrder: sidebarOrderProp,
  loading = false,
}) => {
  const t = useTranslations("eventSidebar");
  const { isOpen, close } = useSidebar();
  const {
    eventId,
    toggleSidebar,
    collapsed,
    menuItems,
    navigateTo,
    prefetch,
    currentUrl,
    canReorder,
    reorderItems,
  } = useEventSidebar(t, collapsedProp, plugins, eventRole, sidebarOrderProp);

  const dragItem = useRef<number | null>(null);
  const dragOverItem = useRef<number | null>(null);
  const [dragOverIndex, setDragOverIndex] = useState<number | null>(null);
  const [isDragging, setIsDragging] = useState(false);

  // On mobile drawer (isOpen), always show expanded regardless of collapsed cookie
  const isCollapsed = collapsed && !isOpen;
  const isDnDEnabled = canReorder && !isCollapsed && !isOpen;

  const handleDragEnd = useCallback(() => {
    const from = dragItem.current;
    const to = dragOverItem.current;
    dragItem.current = null;
    dragOverItem.current = null;
    setDragOverIndex(null);
    setIsDragging(false);
    if (from !== null && to !== null && from !== to) {
      const reordered = [...menuItems];
      const [removed] = reordered.splice(from, 1);
      reordered.splice(to, 0, removed);
      reorderItems(reordered.map((i) => i.url));
    }
  }, [menuItems, reorderItems]);

  return (
    <>
      {/* Mobile backdrop */}
      {isOpen && (
        <div
          className="fixed inset-0 bg-black/50 z-40 lg:hidden"
          onClick={close}
        />
      )}
      <div
        className={`
          fixed inset-y-0 left-0 z-50 lg:relative lg:z-11
          ${isOpen ? "translate-x-0" : "-translate-x-full"} lg:translate-x-0
          w-56.5 ${isCollapsed ? "lg:w-14" : "lg:w-56.5"}
          h-full flex flex-col surface-primary shadow-md px-3 pt-2 pb-6
          transition-all duration-150 ease-out
        `}
      >
        {/* Logo */}
        <div className={`flex flex-row h-12 ${!isCollapsed ? "lg:pl-2" : ""}`}>
          <Logo soloLogo={isCollapsed} />
        </div>

        {/* Archived Banner */}
        {isArchived && (
          <div
            className={`${
              isCollapsed ? "mt-3" : "mt-4"
            } mb-2 rounded-lg hint-orange-bg border hint-orange-outline ${
              isCollapsed ? "p-2" : "px-3 py-2"
            } flex ${isCollapsed ? "flex-col items-center" : "items-center gap-2"} outline-quaternary`}
          >
            <IconArchive
              size={isCollapsed ? 20 : 16}
              className="hint-orange-font shrink-0"
            />
            {!isCollapsed && (
              <span className="body-3-medium hint-orange-font">
                {t("archived")}
              </span>
            )}
          </div>
        )}

        {/* Plan-locked Banner — event plan expired, read-only until renewal */}
        {isPlanLocked && !isArchived && (
          <div
            className={`${
              isCollapsed ? "mt-3" : "mt-4"
            } mb-2 rounded-lg hint-red-bg border hint-red-outline ${
              isCollapsed ? "p-2" : "px-3 py-2"
            } flex ${isCollapsed ? "flex-col items-center" : "items-center gap-2"} outline-quaternary`}
          >
            <IconLock
              size={isCollapsed ? 20 : 16}
              className="hint-red-font shrink-0"
            />
            {!isCollapsed && (
              <span className="body-3-medium hint-red-font">
                {t("planLocked")}
              </span>
            )}
          </div>
        )}

        {/* Search Bar */}
        <div
          data-tour-anchor="tour-global-search"
          className="mt-4 pb-4 border-b outline-quaternary"
        >
          <SearchButton
            collapsed={isCollapsed}
            text={isCollapsed ? "" : t("search")}
            onClick={() =>
              window.dispatchEvent(new Event("openGlobalSearch"))
            }
          />
        </div>

        {/* Menu Items */}
        <nav className="flex-1 mt-4 space-y-1 overflow-y-auto">
          {loading && <MenuSkeleton isCollapsed={isCollapsed} />}
          {!loading && menuItems.map((item, index) => {
            const itemBasePath = `/event/${eventId}/${item.url}`;
            // Segment-boundary match: a bare startsWith would light `app` up
            // for a future `/event/x/apps` too.
            const isParentActive = isPathActive(currentUrl, itemBasePath);
            const isSubmenuOpen = activeSubUrl(currentUrl, eventId ?? "", item) !== null;
            const isDropTarget =
              isDragging &&
              dragOverIndex === index &&
              dragItem.current !== index;
            return (
              <MenuRow
                key={item.url}
                item={item}
                index={index}
                eventId={eventId ?? ""}
                currentUrl={currentUrl}
                isParentActive={isParentActive}
                isSubmenuOpen={isSubmenuOpen}
                isCollapsed={isCollapsed}
                isDnDEnabled={isDnDEnabled}
                isDragging={isDragging}
                isDropTarget={isDropTarget}
                dragItemRef={dragItem}
                dragOverItemRef={dragOverItem}
                setDragOverIndex={setDragOverIndex}
                setIsDragging={setIsDragging}
                onDragEnd={handleDragEnd}
                navigateTo={navigateTo}
                prefetch={prefetch}
              />
            );
          })}
        </nav>

        <div className="hidden lg:block border-t outline-quaternary pt-1">
          <IconButton
            space={isCollapsed ? "space-x-0" : "space-x-3"}
            align={isCollapsed ? "justify-center items-center" : ""}
            padding="py-3"
            icon={IconLayoutSidebarLeftCollapse}
            onClickAction={toggleSidebar}
            fontSize="body-3-medium"
            iconSize={16}
            text={isCollapsed ? "" : t("collapseMenu")}
          />
        </div>
      </div>
    </>
  );
};
